import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { signaturesApi, buildUploadForm, type CertSignature } from "../../../service/certificate"
import { selectBranches, selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"

const MAX_SIZE = 2 * 1024 * 1024
const ACCEPTED = ".png,.jpg,.jpeg,.webp"

// Signatures master data — matches RMS Signatures page:
//  - Image thumbnail in table (64×24 px, contains signature)
//  - Branch column (shows campus code or "All branches")
//  - Branch filter dropdown
//  - Create: file required + signer name + title + branch + active
//  - Edit: metadata-only (name, title, branch, active); image is immutable
const CertSignatures = () => {
  // Branches come from global Redux — no local fetch needed.
  const branches = useSelector(selectBranches)
  const currentBranch = useSelector(selectCurrentBranch)

  const [items, setItems] = useState<CertSignature[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  // Local branch filter: defaults to the globally selected branch; user can override within this page.
  const [branchFilter, setBranchFilter] = useState(currentBranch?._id || "")
  const [includeInactive, setIncludeInactive] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [editing, setEditing] = useState<CertSignature | "new" | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // When global branch changes, sync the local filter.
  useEffect(() => { setBranchFilter(currentBranch?._id || "") }, [currentBranch?._id])

  const fetchItems = async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = {}
      if (includeInactive) params.includeInactive = 1
      if (branchFilter) params.branch = branchFilter
      const res = await signaturesApi.list(params)
      if (res.success) setItems(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load signatures")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchItems() }, [includeInactive, branchFilter]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1) }, [items.length])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(s =>
      [s.signerName, s.signerTitle].some(v => String(v || "").toLowerCase().includes(q))
    )
  }, [items, search])

  const handleRestore = async (id: string) => {
    try {
      await signaturesApi.restore(id)
      toast.success("Restored")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Restore failed")
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await signaturesApi.remove(deleteId)
      toast.success("Deleted")
      setDeleteId(null)
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      render: (url: string, record: CertSignature) =>
        url ? (
          <img
            src={url}
            alt={record.signerName}
            style={{ height: 40, width: 128, objectFit: "contain", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, padding: 2 }}
            onError={(e: any) => { e.currentTarget.style.opacity = "0.3" }}
          />
        ) : (
          <span className="text-muted small">—</span>
        ),
    },
    { title: "Signer", dataIndex: "signerName" },
    { title: "Title", dataIndex: "signerTitle" },
    {
      title: "Branch",
      dataIndex: "branch",
      render: (b: CertSignature["branch"]) =>
        b ? (
          <span>{b.campusNo || b.name}</span>
        ) : (
          <span className="text-muted small">All branches</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v === false ? (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ) : (
          <span className="badge bg-success-transparent text-success">Active</span>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: CertSignature) =>
        record.isActive === false ? (
          <button className="btn btn-sm btn-outline-success" onClick={() => handleRestore(record._id)}>
            <i className="ti ti-restore me-1" />Restore
          </button>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(record)}>
              <i className="ti ti-edit" />
            </Link>
            <Link to="#" className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(record._id)}>
              <i className="ti ti-trash" />
            </Link>
          </div>
        ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Signatures</h3>
            <p className="text-muted mb-0">
              Signer name + title + image. Used by the designer to place named signatures on certificate templates.
            </p>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <input
              className="form-control"
              style={{ maxWidth: 220 }}
              placeholder="Search name or title…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {branches.length > 0 && (
              <select
                className="form-select"
                style={{ maxWidth: 200 }}
                value={branchFilter}
                onChange={e => setBranchFilter(e.target.value)}
              >
                <option value="">All branches</option>
                {branches.map(b => (
                  <option key={b._id} value={b._id}>{b.campusNo ? `${b.campusNo} — ${b.name}` : b.name}</option>
                ))}
              </select>
            )}
            <div className="form-check form-switch mb-0 me-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="cert_sig_inactive"
                checked={includeInactive}
                onChange={e => setIncludeInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="cert_sig_inactive">Show inactive</label>
            </div>
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <i className="ti ti-square-rounded-plus me-2" />Add Signature
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={visible}
              columns={columns}
              loading={loading}
              pagination={{
                currentPage: page,
                limit: pageSize,
                total: visible.length,
                onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) },
              }}
            />
          </div>
        </div>
      </div>

      {editing && (
        <SignatureModal
          editing={editing}
          branches={branches}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchItems() }}
        />
      )}

      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Signature</h4>
                  <p className="text-muted">This soft-deletes the record. Past certificates that used this signature are unaffected.</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

const SignatureModal = ({
  editing,
  branches,
  onClose,
  onSaved,
}: {
  editing: CertSignature | "new"
  branches: { _id: string; name: string; campusNo?: string }[]
  onClose: () => void
  onSaved: () => void
}) => {
  const isEdit = editing !== "new"
  const [signerName, setSignerName] = useState(isEdit ? editing.signerName : "")
  const [signerTitle, setSignerTitle] = useState(isEdit ? editing.signerTitle : "")
  const [branch, setBranch] = useState(isEdit ? (editing.branch?._id || "") : "")
  const [active, setActive] = useState(isEdit ? editing.isActive !== false : true)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) { setFile(null); return }
    if (f.size > MAX_SIZE) {
      setError(`File too large: ${(f.size / 1024 / 1024).toFixed(1)} MB exceeds 2 MB`)
      setFile(null); e.target.value = ""; return
    }
    if (!/\.(png|jpe?g|webp)$/i.test(f.name)) {
      setError("Only PNG, JPG, or WEBP allowed")
      setFile(null); e.target.value = ""; return
    }
    setError(null)
    setFile(f)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!signerName.trim()) { setError("Signer name is required"); return }
    if (!signerTitle.trim()) { setError("Signer title is required"); return }
    if (!isEdit && !file) { setError("Pick an image file first"); return }
    setSaving(true); setError(null)
    try {
      if (isEdit) {
        await signaturesApi.update(editing._id, {
          signerName: signerName.trim(),
          signerTitle: signerTitle.trim(),
          branch: branch || null,
          isActive: active,
        })
        toast.success("Updated")
      } else {
        const fd = buildUploadForm(
          { signerName: signerName.trim(), signerTitle: signerTitle.trim(), branch: branch || "" },
          file,
          "image"
        )
        await signaturesApi.create(fd)
        toast.success("Created")
      }
      onSaved()
    } catch (err: any) {
      setError(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{isEdit ? "Edit signature" : "New signature"}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                {/* Image: editable on create, read-only on edit */}
                {isEdit ? (
                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    {editing.imageUrl && (
                      <div className="mb-1">
                        <img
                          src={editing.imageUrl}
                          alt={editing.signerName}
                          style={{ height: 64, maxWidth: "100%", objectFit: "contain", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, padding: 4 }}
                        />
                      </div>
                    )}
                    <small className="text-muted">To change the image, delete this signature and add a new one.</small>
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">Image *</label>
                    <input ref={fileInputRef} type="file" className="form-control" accept={ACCEPTED} onChange={onPickFile} />
                    <small className="text-muted">
                      PNG / JPG / WEBP, up to 2 MB. Transparent backgrounds render best.
                      {file ? ` Selected: ${file.name} (${(file.size / 1024).toFixed(0)} KB)` : ""}
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Signer name *</label>
                  <input type="text" className="form-control" value={signerName} onChange={e => setSignerName(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Signer title *</label>
                  <input type="text" className="form-control" value={signerTitle} onChange={e => setSignerTitle(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Campus</label>
                  <select className="form-select" value={branch} onChange={e => setBranch(e.target.value)}>
                    <option value="">All branches</option>
                    {branches.map(b => (
                      <option key={b._id} value={b._id}>{b.campusNo ? `${b.campusNo} — ${b.name}` : b.name}</option>
                    ))}
                  </select>
                  <small className="text-muted">Leave blank for an all-branches signer (Director, etc.).</small>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="cert_sig_active" checked={active} onChange={e => setActive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="cert_sig_active">Active</label>
                </div>

                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : isEdit ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default CertSignatures
